import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cn from 'classnames';
import CONST from '../constants/invoice-constants';
import { withCurrency } from '../utils/send-money-utils';
import { getWordMaxLength } from '../utils/string-utils';

const EXPECTED_FIELDS = [
  'order_id',
  'total',
  'tax',
  'amount',
  'description',
  'created_at',
  'expires_at',
  'recipient',
  'send_to_email',
  'send_to_sms',
  'status',
  'attributes'
];

const EXCLUDED_FIELDS = [
  'BR_',
  '_'
];
const OrderDetails = ({ details, keyField = 'key' }) => {
  const orderDetails = useMemo(() => {
    if(!details || !Array.isArray(details)) {
      return { attributes: [] };
    }

    return details.reduce((result, field) => {
      const key = field[keyField];
      const value = field.value;

      if(EXPECTED_FIELDS.includes(key)) {
        result[key] = value;
      } else if(EXCLUDED_FIELDS.every(excluded => !key.startsWith(excluded))) {
        result.attributes.push({ name: key, value });
      }
      return result;
    }, { attributes: [] });
  }, [details]);

  const { order_id, total, tax, amount, description, created_at, expires_at, recipient, send_to_email, send_to_sms, status, attributes } = orderDetails;
  const recipientInfo = [recipient, send_to_email, send_to_sms].filter(Boolean);

  return (
    <div className="layer invoice-details">
      {order_id && (
        <div className="description-list -fixed-title -x-condensed">
          <div className="description-list_item">
            <div className="description-list_title">Order ID</div>
            <div className="js-transaction-result-type-text description-list_value">{order_id}</div>
          </div>
        </div>
      )}

      <div className="description-list -fixed-title -x-condensed">
        <div className="description-list_item">
          <div className="description-list_title">Total</div>
          <div className="js-transaction-result-type-text description-list_value">{withCurrency(amount, 'USD')}</div>
        </div>
        {tax && (
          <div className="description-list_item">
            <div className="description-list_title">Tax</div>
            <div className="js-transaction-result-type-text description-list_value">{withCurrency(tax, 'USD')}</div>
          </div>
        )}
        {total && (
          <div className="description-list_item">
            <div className="description-list_title">Amount</div>
            <div className="js-transaction-result-type-text description-list_value">{withCurrency(total, 'USD')}</div>
          </div>
        )}
        {description && (
          <div className="description-list_item">
            <div className="description-list_title">Description</div>
            <div className="js-transaction-result-type-text description-list_value">{description}</div>
          </div>
        )}
      </div>
      <div className="description-list -fixed-title -x-condensed">
        <div className="description-list_item">
          <div className="description-list_title">Created</div>
          <div className="js-transaction-result-type-text description-list_value">{moment(created_at).format('MMMM D, YYYY')}</div>
        </div>
        <div className="description-list_item">
          <div className="description-list_title">Expires</div>
          <div className="js-transaction-result-type-text description-list_value">{moment(expires_at).format('MMMM D, YYYY')}</div>
        </div>
      </div>
      {recipientInfo.length > 0 && (
        <div className="description-list -fixed-title -x-condensed">
          <div className="description-list_item">
            <div className="description-list_title">Recipient</div>
            <div className="js-transaction-result-type-text description-list_value">
              <div className={cn('description-list_item-primary', {
                '-lowercase': recipientInfo[0] === send_to_email
              })}>
                {recipientInfo[0] || 'Not Specified'}
              </div>
              <div className={cn('description-list_item-secondary', {
                '-lowercase': recipientInfo[1] === send_to_email
              })}>
                {recipientInfo[1]} 
              </div>
            </div>
          </div>
        </div>
      )}
      {status && (
        <div className="description-list -fixed-title -x-condensed">
          <div className="description-list_item">
            <div className="description-list_title">Status</div>
            <div className={`description-list_value font-weight-bold color-${CONST.INVOICE_STATUS_COLOR[status]} -capitalize`}>
              {CONST.INVOICE_STATUS[status]}
            </div>
          </div>
        </div>
      )}
      <div className="description-list -fixed-title -x-condensed">
        {Array.isArray(attributes) && attributes.length > 0 && attributes.map((attribute) => (
          <div className="description-list_item">
            <div className={cn('description-list_title -capitalize', {
              'word-break-word': attribute.name.length > 19 && getWordMaxLength(attribute.name) <= 19,
              'word-break-all': attribute.name.length > 19 && getWordMaxLength(attribute.name) > 19
            })}>{attribute.name}</div>
            <div className={cn('js-transaction-result-type-text description-list_value', {
              'word-break-word': attribute.value.length > 30 && getWordMaxLength(attribute.value) <= 30,
              'word-break-all': attribute.value.length > 30 && getWordMaxLength(attribute.value) > 30
            })}>{attribute.value}</div>
          </div>
        ))}
      </div>
    </div>
  ); 
};

OrderDetails.propTypes = {
  details: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, value: PropTypes.string })),
  keyField: PropTypes.string
};

export default OrderDetails;